/**
 * The one place that decides what state an employee is in.
 *
 * Three mutually exclusive states, derived from two independent fields:
 *
 *   active   — no flags set. Can log in, and is the only state that should ever
 *              appear in a picker where you are choosing who does work next.
 *   blocked  — `isBlock`. A reversible suspension; the person still works here.
 *   removed  — `deactivatedAt`. They left, their open work was handed over, and
 *              the record is kept for history.
 *
 * `removed` wins over `blocked` because removal sets BOTH fields
 * (employeeRemovalService sets isBlock: true alongside deactivatedAt), so
 * checking isBlock first would label every leaver "blocked".
 *
 * This lived duplicated in StaffDataTable and ManageStaffTable, which is how
 * the two disagreed about what a row meant. Import it; do not re-derive it.
 */

export const STAFF_STATUS = {
  ACTIVE: "active",
  BLOCKED: "blocked",
  REMOVED: "removed",
};

export const getStaffStatus = (employee) => {
  if (employee?.deactivatedAt) return STAFF_STATUS.REMOVED;
  if (employee?.isBlock) return STAFF_STATUS.BLOCKED;
  return STAFF_STATUS.ACTIVE;
};

export const isActiveStaff = (employee) =>
  getStaffStatus(employee) === STAFF_STATUS.ACTIVE;

/**
 * Active people only.
 *
 * Returns a plain array even when given undefined, because every caller feeds
 * this straight into `.map()` to build select options and an undefined there is
 * a crash rather than an empty dropdown.
 */
export const filterActiveStaff = (staff) => (staff || []).filter(isActiveStaff);

/**
 * Anyone who still works here — active or suspended, never a leaver.
 *
 * The frontend half of the backend's ASSIGNABLE_EMPLOYEE. Use it only where a
 * human picks a named person AND can see their status on screen; Manage Team
 * shows a "Suspended" badge for exactly this reason. Everywhere else keeps
 * using filterActiveStaff, because a blocked employee cannot log in and work
 * handed to them sits untouched until somebody unblocks them.
 *
 * Same undefined-safe return as filterActiveStaff, for the same reason.
 */
export const isAssignableStaff = (employee) =>
  getStaffStatus(employee) !== STAFF_STATUS.REMOVED;

export const filterAssignableStaff = (staff) =>
  (staff || []).filter(isAssignableStaff);

/** How many people are in each state — used for the tab counts. */
export const countStaffByStatus = (staff = []) =>
  (staff || []).reduce(
    (acc, employee) => {
      acc[getStaffStatus(employee)] += 1;
      return acc;
    },
    { active: 0, blocked: 0, removed: 0 }
  );
